import { type FC, memo, type ReactNode } from "react";
import { Button } from "~/common/components/Button/Button";
import { Dialog, DialogActions, DialogContent, type DialogProps } from "@mui/material";
import { Stacker } from "./Stacker/Stacker";

interface Props {
  label?: ReactNode;
  okLabel?: ReactNode;
  cancelLabel?: ReactNode;
  onOk?: () => void;
  onCancel?: () => void;
  onClose: () => void;
  children?: ReactNode;
}

/**
 * Use either label or children - label takes precedence over children
 */
const ConfirmationModalExtended: FC<Props> = ({ label, okLabel, cancelLabel, onCancel, onOk, onClose, children }) => {
  const handleClose: DialogProps["onClose"] = (e: React.MouseEvent<HTMLAnchorElement>, reason) => {
    e.preventDefault();
    e.stopPropagation();
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return; // Prevent closing on outside click or escape key
    }
    onClose();
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: (theme) => ({
            width: "312px !important",
            [theme.breakpoints.up("desktop-s")]: {
              width: "720px !important",
            },
          }),
        },
      }}
    >
      <DialogContent> {label ? <p className={"headline-small"}>{label}</p> : children}</DialogContent>
      <DialogActions>
        <Stacker gap={8} direction={"row"}>
          {onCancel && cancelLabel && (
            <Button variant={"text"} onClick={onCancel} data-test-id={"confirmation-modal-cancel"}>
              {cancelLabel}
            </Button>
          )}
          {onOk && okLabel && (
            <Button onClick={onOk} data-test-id={"confirmation-modal-ok"}>
              {okLabel}
            </Button>
          )}
        </Stacker>
      </DialogActions>
    </Dialog>
  );
};

export default memo(ConfirmationModalExtended);
